/**
 * Campo de formulário: rótulo acima do input e três estados visuais — normal,
 * foco (borda da marca com halo) e erro (borda de perigo). Refino final
 * (23/09/2026): rótulo 12/700 em caixa normal, valor 16/500, e o campo
 * preenchido fica com a borda da marca; senha ganha o olho que mostra/oculta.
 *
 * O erro não é só cor: o texto vai em `role="alert"` e o input aponta para ele
 * por `aria-describedby`. Estado que depende só de cor não existe para quem
 * usa leitor de tela, nem para quem não distingue as cores.
 */
import { useId, useState, type InputHTMLAttributes } from 'react';

import { Icon } from '../../ui/icons';

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  error?: string | undefined;
  required?: boolean;
}

export function Field({ label, error, required, type, ...input }: FieldProps) {
  const id = useId();
  const idErro = `${id}-erro`;
  const senha = type === 'password';
  const [mostrar, setMostrar] = useState(false);
  const preenchido = input.value !== undefined && String(input.value).length > 0;

  const classes = ['nv-field', error ? 'nv-field--error' : '', preenchido ? 'nv-field--preenchido' : '', senha ? 'nv-field--senha' : ''].filter(Boolean).join(' ');

  const campo = (
    <input
      id={id}
      className="nv-field__input"
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? idErro : undefined}
      required={required}
      type={senha && mostrar ? 'text' : type}
      {...input}
    />
  );

  return (
    <div className={classes}>
      <label className="nv-field__label" htmlFor={id}>
        {label}
        {required && <span className="nv-field__required" aria-hidden="true"> *</span>}
      </label>
      {senha ? (
        <span className="nv-field__linha">
          {campo}
          <button
            type="button"
            className={mostrar ? 'nv-field__olho nv-field__olho--aberto' : 'nv-field__olho'}
            onClick={() => setMostrar((v) => !v)}
            aria-label={mostrar ? 'Ocultar senha' : 'Mostrar senha'}
            aria-pressed={mostrar}
          >
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
              <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
              <circle cx="12" cy="12" r="3" />
              <path className="nv-field__olho-risco" d="M4 4l16 16" />
            </svg>
          </button>
        </span>
      ) : (
        campo
      )}
      {error && (
        <p className="nv-field__error" id={idErro} role="alert">
          <Icon name="aviso" size={14} />
          {error}
        </p>
      )}
    </div>
  );
}
